import React, { useCallback, useEffect, useState } from 'react';
import { Navigation } from './components/Navigation';
import { ParkingLot } from './components/ParkingLot';
import { PresenterNotes } from './components/PresenterNotes';
import { ParkingLotItem } from './types';
import { PAGES } from './pages/PageData';
import { ChevronLeft, ChevronRight } from 'lucide-react';
// We will import page components here as we build them
import { Page1, Page2, Page3, Page4, Page5, Page6 } from './pages/Section1_Intro';
import { Page7, Page8, Page9 } from './pages/Section2_Deal';
import { Page10, Page11 } from './pages/Section3_DemoPrep';
import { DemoPage } from './pages/DemoPage';
import { ArchitecturePage } from './pages/ArchitecturePage';
import { Page15, Page16, Page17, Page18, Page19 } from './pages/Section5_Close';
import { CheatSheet } from './pages/CheatSheet';
import { AppendixQA, NinetyDayPlan } from './pages/AppendixPages';
export function App() {
  const [currentPage, setCurrentPage] = useState(0);
  const [isParkingLotOpen, setIsParkingLotOpen] = useState(false);
  const [parkingLotItems, setParkingLotItems] = useState<ParkingLotItem[]>([]);
  const [notesVisibility, setNotesVisibility] = useState<'shown' | 'minimised' | 'hidden'>('shown');
  const totalPages = PAGES.length - 1; // Exclude cheat sheet from normal flow
  const handleNext = useCallback(() => {
    if (currentPage < totalPages - 1) setCurrentPage(p => p + 1);
  }, [currentPage, totalPages]);
  const handlePrev = useCallback(() => {
    if (currentPage > 0) setCurrentPage(p => p - 1);
  }, [currentPage]);
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        // Don't trigger if typing in an input
        if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
        handlePrev();
      } else if (e.key === 'p' || e.key === 'P') {
        if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
        setIsParkingLotOpen(prev => !prev);
      } else if (e.key === 'h' || e.key === 'H') {
        if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
        setNotesVisibility(prev => {
          if (prev === 'shown') return 'minimised';
          if (prev === 'minimised') return 'hidden';
          return 'shown';
        });
      } else if (e.key === 'c' && e.ctrlKey) {
        // Secret shortcut for cheat sheet
        setCurrentPage(20);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev]);
  const addParkingLotItem = (question: string) => {
    setParkingLotItems(prev => [...prev, {
      id: Date.now().toString(),
      question,
      isResolved: false,
      returnToThis: false,
      timestamp: Date.now()
    }]);
  };
  const toggleResolve = (id: string) => {
    setParkingLotItems(prev => prev.map(item => item.id === id ? {
      ...item,
      isResolved: !item.isResolved
    } : item));
  };
  const toggleReturn = (id: string) => {
    setParkingLotItems(prev => prev.map(item => item.id === id ? {
      ...item,
      returnToThis: !item.returnToThis
    } : item));
  };
  const renderPage = () => {
    switch (currentPage) {
      case 0:
        return <Page1 data-id="element-0" />;
      case 1:
        return <Page2 data-id="element-1" />;
      case 2:
        return <Page3 data-id="element-2" />;
      case 3:
        return <Page4 data-id="element-3" />;
      case 4:
        return <Page5 data-id="element-4" />;
      case 5:
        return <Page7 data-id="element-5" />;
      case 6:
        return <Page8 data-id="element-6" />;
      case 7:
        return <Page9 data-id="element-7" />;
      case 8:
        return <Page10 data-id="element-8" />;
      case 9:
        return <Page11 data-id="element-9" />;
      case 10:
        return <DemoPage data-id="element-10" />;
      case 11:
        return <ArchitecturePage data-id="element-11" />;
      case 12:
        return <Page15 data-id="element-12" />;
      case 13:
        return <Page18 data-id="element-13" />;
      case 14:
        return <Page19 data-id="element-14" />;
      case 15:
        return <Page17 data-id="element-15" />;
      case 16:
        return <Page16 data-id="element-16" />;
      case 17:
        return <AppendixQA data-id="element-17" />;
      case 18:
        return <Page6 data-id="element-18" />;
      case 19:
        return <NinetyDayPlan data-id="element-19" />;
      case 20:
        return <CheatSheet data-id="element-20" />;
      default:
        return <Page1 data-id="element-21" />;
    }
  };
  const currentPageData = PAGES[currentPage];
  return <div className="min-h-screen bg-sierra-bg text-sierra-dark flex flex-col font-sans" data-id="element-22">
      <Navigation currentPage={currentPage} totalPages={totalPages} onNavigate={setCurrentPage} onToggleParkingLot={() => setIsParkingLotOpen(!isParkingLotOpen)} parkingLotCount={parkingLotItems.filter(i => !i.isResolved).length} onDemoJump={() => setCurrentPage(10)} data-id="element-23" />

      <main className="flex-1 relative pt-14 flex flex-col items-center justify-center overflow-hidden" data-id="element-24">
        {/* Page Content Wrapper with simple fade */}
        <div key={currentPage} className="w-full h-full max-w-6xl mx-auto p-8 md:p-12 flex flex-col justify-center animate-fade-in" data-id="element-25">
          {renderPage()}
        </div>
      </main>

      {/* Pagination Controls (Bottom Left) */}
      {currentPage !== 20 && <div className="fixed bottom-6 left-6 flex items-center gap-2 z-40" data-id="element-26">
          <button onClick={handlePrev} disabled={currentPage === 0} className="p-2 rounded-full bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-sierra-dark disabled:opacity-30 disabled:hover:bg-white transition-all sierra-shadow" data-id="element-27">
            <ChevronLeft className="w-5 h-5" data-id="element-28" />
          </button>
          <span className="text-xs font-medium text-gray-400 w-12 text-center" data-id="element-29">
            {currentPage + 1} / {totalPages}
          </span>
          <button onClick={handleNext} disabled={currentPage === totalPages - 1} className="p-2 rounded-full bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-sierra-dark disabled:opacity-30 disabled:hover:bg-white transition-all sierra-shadow" data-id="element-30">
            <ChevronRight className="w-5 h-5" data-id="element-31" />
          </button>
        </div>}

      <ParkingLot isOpen={isParkingLotOpen} onClose={() => setIsParkingLotOpen(false)} items={parkingLotItems} onAddItem={addParkingLotItem} onToggleResolve={toggleResolve} onToggleReturn={toggleReturn} data-id="element-32" />

      <PresenterNotes notes={currentPageData?.notes || []} timebox={currentPageData?.timebox} visibility={notesVisibility} data-id="element-33" />
    </div>;
}